/**
 * Align tool — place one splat onto another by clicking matching features in
 * both.
 *
 * The use case: a space is re-captured (better camera, better pipeline) and
 * the new splat must take the old one's place in an existing scene, so that
 * everything authored in world space — walk boundaries, levels, stairs,
 * markers, the camera animation — keeps working untouched.
 *
 * Why picks rather than automatic registration: the two captures rarely cover
 * the same extent (a phone pass may include corridors the original never saw)
 * and an SfM reconstruction carries no metric scale, so automatic methods have
 * to search scale and orientation and can settle confidently into a wrong
 * answer. Clicked correspondences determine the similarity transform outright.
 * See align-math.ts (unit-tested in tools/align-math-test.mjs).
 *
 * Flow: click N features on the splat being MOVED, then the same N features in
 * the same order on the TARGET. One splat at a time, so each can be framed (or
 * the other hidden) while picking it.
 *
 * Three pairs are the minimum that determines the transform, but three leave a
 * similarity only 2 residual degrees of freedom, so a mis-picked or
 * out-of-order feature can hide. So any number of pairs is accepted (4-5 is
 * much steadier than 3), the fit is previewed before it is applied, and each
 * pair's own error is reported — a pick that went to the wrong feature shows
 * up as one large residual, flagged red in the viewport, instead of quietly
 * rotating the whole result.
 */

import { Container } from '@playcanvas/pcui';
import { Entity, Mesh, MeshInstance, PRIMITIVE_LINES, Quat, SEMANTIC_COLOR, SEMANTIC_POSITION, ShaderMaterial, Vec3 } from 'playcanvas';
import { seedLineMesh } from '../line-mesh';

import {
    composeTransform, pairResiduals, similarityFromPairs, type Similarity, type V3
} from '../align-math';
import type { Events } from '../../events';
import type { Scene } from '../../scene';
import { vertexShader, fragmentShader } from '../../shaders/debug-shader';
import type { Splat } from '../../splat';

type RGB = [number, number, number];
const SRC_RGB: RGB = [0, 200, 255];    // cyan — picks on the splat being moved
const DST_RGB: RGB = [255, 150, 50];   // orange — picks on the target splat
const LINK_RGB: RGB = [120, 120, 120]; // pair connector
const BAD_RGB: RGB = [255, 60, 60];    // the worst pair, when it stands out

/** Fewest pairs that determine a similarity transform. */
const MIN_PAIRS = 3;

class AlignTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, _canvasContainer: Container) {
        const canvas = scene.canvas;
        let active = false;

        // All picks on the source splat first, then the matching picks on the
        // target, in the same order. Phase flips when the user says so (or on
        // the first click that lands on a different splat).
        let srcPicks: Vec3[] = [];
        let dstPicks: Vec3[] = [];
        let phase: 'source' | 'target' = 'source';
        let sourceSplat: Splat | null = null;
        let targetSplat: Splat | null = null;
        /** Pre-align transform of the source splat, for 'align.undo'. */
        let previous: { splat: Splat; position: Vec3; rotation: Quat; scale: Vec3 } | null = null;

        // ---- viewport overlay (self-contained; not a scene Element) -------
        let entity: Entity | null = null;
        let mesh: Mesh | null = null;

        const buildOverlay = () => {
            if (entity) return;
            const material = new ShaderMaterial({
                uniqueName: 'alignToolMaterial',
                attributes: {
                    vertex_position: SEMANTIC_POSITION,
                    vertex_color: SEMANTIC_COLOR
                },
                vertexWGSL: vertexShader,
                fragmentWGSL: fragmentShader
            });
            material.depthWrite = false;
            material.depthTest = false;
            material.update();

            mesh = new Mesh(scene.graphicsDevice);
            mesh.primitive[0] = { baseVertex: 0, type: PRIMITIVE_LINES, base: 0, count: 0 };
            seedLineMesh(mesh);
            const meshInstance = new MeshInstance(mesh, material, null);
            meshInstance.cull = false;

            entity = new Entity('alignTool');
            entity.addComponent('render', {
                meshInstances: [meshInstance],
                layers: [scene.gizmoLayer.id]
            });
            scene.app.root.addChild(entity);
        };

        /** How many complete pairs exist (picks matched up by order). */
        const pairCount = () => Math.min(srcPicks.length, dstPicks.length);

        /** Current best fit from the complete pairs — null until MIN_PAIRS. */
        const currentFit = (): { sim: Similarity; errors: number[] } | null => {
            const n = pairCount();
            if (n < MIN_PAIRS) return null;
            const src: V3[] = srcPicks.slice(0, n).map(p => [p.x, p.y, p.z]);
            const dst: V3[] = dstPicks.slice(0, n).map(p => [p.x, p.y, p.z]);
            const sim = similarityFromPairs(src, dst);
            if (!sim) return null;
            return { sim, errors: pairResiduals(sim, src, dst) };
        };

        const rebuildOverlay = () => {
            if (!mesh) return;
            const fit = currentFit();
            const worst = fit ? fit.errors.indexOf(Math.max(...fit.errors)) : -1;
            // Only call out a bad pair when it is clearly the outlier.
            const flagWorst = !!fit && fit.errors.length > MIN_PAIRS &&
                fit.errors[worst] > 3 * (fit.errors.reduce((a, b) => a + b, 0) / fit.errors.length);

            const pos: number[] = [];
            const col: number[] = [];
            const line = (a: Vec3, b: Vec3, rgb: RGB) => {
                pos.push(a.x, a.y, a.z, b.x, b.y, b.z);
                for (let k = 0; k < 2; k++) col.push(rgb[0], rgb[1], rgb[2], 255);
            };
            const half = Math.max(0.03, (scene.camera.sceneRadius ?? 1) * 0.02);
            const cross = (p: Vec3, rgb: RGB, size: number) => {
                line(new Vec3(p.x - size, p.y, p.z), new Vec3(p.x + size, p.y, p.z), rgb);
                line(new Vec3(p.x, p.y - size, p.z), new Vec3(p.x, p.y + size, p.z), rgb);
                line(new Vec3(p.x, p.y, p.z - size), new Vec3(p.x, p.y, p.z + size), rgb);
            };
            // Pick i is drawn with i+1 nested crosses, so the ORDER is
            // countable in the viewport — which is what keeps the second pass
            // matching the first.
            const bad = (i: number) => flagWorst && i === worst;
            srcPicks.forEach((p, i) => {
                for (let k = 0; k <= i; k++) cross(p, bad(i) ? BAD_RGB : SRC_RGB, half * (1 - k * 0.18));
            });
            dstPicks.forEach((p, i) => {
                for (let k = 0; k <= i; k++) cross(p, bad(i) ? BAD_RGB : DST_RGB, half * (1 - k * 0.18));
            });
            for (let i = 0; i < pairCount(); i++) {
                line(srcPicks[i], dstPicks[i], bad(i) ? BAD_RGB : LINK_RGB);
            }

            if (pos.length === 0) {
                mesh.primitive[0].count = 0;
                if (entity) entity.enabled = false;
            } else {
                mesh.setPositions(pos);
                mesh.setColors32(new Uint8Array(col));
                mesh.update(PRIMITIVE_LINES);
                if (entity) entity.enabled = mesh.primitive[0].count > 0;
            }
            scene.forceRender = true;
        };

        const fireChanged = () => {
            const fit = currentFit();
            events.fire('align.changed', {
                phase,
                src: srcPicks.length,
                dst: dstPicks.length,
                pairs: pairCount(),
                minPairs: MIN_PAIRS,
                sourceName: sourceSplat?.name ?? null,
                targetName: targetSplat?.name ?? null,
                canSwitch: phase === 'source' && srcPicks.length >= MIN_PAIRS,
                canApply: pairCount() >= MIN_PAIRS && srcPicks.length === dstPicks.length,
                canUndo: !!previous,
                fitScale: fit ? fit.sim.scale : null,
                worstError: fit ? Math.max(...fit.errors) : null,
                worstIndex: fit ? fit.errors.indexOf(Math.max(...fit.errors)) : -1,
                degenerate: fit ? fit.sim.degenerate : false
            });
        };

        const status = (msg: string) => events.fire('align.status', msg);

        const guidance = () => {
            if (phase === 'source') {
                const n = srcPicks.length;
                const need = Math.max(0, MIN_PAIRS - n);
                return `Feature ${n + 1} on the splat you are MOVING (${sourceSplat?.name ?? 'selected'}).` +
                    (need > 0
                        ? ` ${need} more needed, then switch to the target.`
                        : ' Add more for a steadier fit, or press "Now pick target" when done.');
            }
            const n = dstPicks.length;
            if (n < srcPicks.length) {
                return `Target splat: click feature ${n + 1} of ${srcPicks.length} — the SAME feature, same order as on the moving splat.`;
            }
            const fit = currentFit();
            const worst = fit ? (Math.max(...fit.errors) * 100).toFixed(1) : '?';
            return `All ${srcPicks.length} matched. Fit scale ×${fit ? fit.sim.scale.toFixed(4) : '?'}, worst pair ${worst} cm. Press Apply, or undo a bad pick first.`;
        };

        const resetPicks = () => {
            srcPicks = [];
            dstPicks = [];
            phase = 'source';
            targetSplat = null;
            rebuildOverlay();
            fireChanged();
        };

        const switchToTarget = () => {
            if (phase === 'target') return;
            if (srcPicks.length < MIN_PAIRS) {
                status(`Pick at least ${MIN_PAIRS} features on the moving splat first.`);
                return;
            }
            phase = 'target';
            fireChanged();
            status(guidance());
        };

        const apply = () => {
            if (srcPicks.length !== dstPicks.length) {
                status(`${srcPicks.length} picks on the moving splat but ${dstPicks.length} on the target — they must match one for one.`);
                return;
            }
            const fit = currentFit();
            if (!fit) {
                status(`Need at least ${MIN_PAIRS} matched pairs.`);
                return;
            }
            if (fit.sim.degenerate) {
                status('Your picks are nearly in a straight line, which leaves the rotation undetermined. Add a pair well off that line.');
                return;
            }
            const splat = sourceSplat!;
            const ent = splat.entity;
            previous = {
                splat,
                position: ent.getLocalPosition().clone(),
                rotation: ent.getLocalRotation().clone(),
                scale: ent.getLocalScale().clone()
            };
            const cur = {
                position: [previous.position.x, previous.position.y, previous.position.z] as V3,
                rotation: [previous.rotation.x, previous.rotation.y, previous.rotation.z, previous.rotation.w] as
                    [number, number, number, number],
                scale: [previous.scale.x, previous.scale.y, previous.scale.z] as V3
            };
            const next = composeTransform(fit.sim, cur);
            splat.move(
                new Vec3(next.position[0], next.position[1], next.position[2]),
                new Quat(next.rotation[0], next.rotation[1], next.rotation[2], next.rotation[3]),
                new Vec3(next.scale[0], next.scale[1], next.scale[2])
            );
            scene.boundDirty = true;
            scene.forceRender = true;

            const worstM = Math.max(...fit.errors);
            status(`Aligned from ${pairCount()} pairs. Scale ×${fit.sim.scale.toFixed(4)}, worst pair ${(worstM * 100).toFixed(1)} cm.${
                worstM > 0.3 ? ' That is loose — "Undo last align", then re-pick the pair flagged red.' : ''}`);
            events.fire('align.applied', { scale: fit.sim.scale, worst: worstM });
            resetPicks();
        };

        const onPointerDown = async (e: PointerEvent) => {
            if (!active || e.button !== 0) return;
            e.stopPropagation();
            e.preventDefault();

            const rect = canvas.getBoundingClientRect();
            const nx = (e.clientX - rect.left) / rect.width;
            const ny = (e.clientY - rect.top) / rect.height;
            let hit: { position: Vec3; splat: Splat } | null = null;
            try {
                hit = await scene.camera.intersect(nx, ny) as { position: Vec3; splat: Splat } | null;
            } catch (err) {
                hit = null;
            }
            if (!hit?.position || !hit.splat) {
                status('Nothing there — click directly on a visible surface of the splat.');
                return;
            }

            if (phase === 'source') {
                if (hit.splat !== sourceSplat) {
                    // Clicking the other splat once the minimum is in almost
                    // always means "I'm done with this side" — take it.
                    if (srcPicks.length >= MIN_PAIRS) {
                        phase = 'target';
                        targetSplat = hit.splat;
                        dstPicks.push(hit.position.clone());
                    } else {
                        status(`That is a different splat. Pick at least ${MIN_PAIRS} features on "${sourceSplat?.name}" (the one being moved) first.`);
                        return;
                    }
                } else {
                    srcPicks.push(hit.position.clone());
                }
            } else {
                if (hit.splat === sourceSplat) {
                    status('That is the splat being moved. Click the matching feature on the TARGET splat.');
                    return;
                }
                if (targetSplat && hit.splat !== targetSplat) {
                    status(`Stay on "${targetSplat.name}" — every target pick must be on the same splat.`);
                    return;
                }
                if (dstPicks.length >= srcPicks.length) {
                    status(`You already matched all ${srcPicks.length} features. Press Apply, or undo a pick to redo it.`);
                    return;
                }
                targetSplat = hit.splat;
                dstPicks.push(hit.position.clone());
            }
            rebuildOverlay();
            fireChanged();
            status(guidance());
        };

        const undoPick = () => {
            if (phase === 'target' && dstPicks.length > 0) {
                dstPicks.pop();
                if (dstPicks.length === 0) targetSplat = null;
            } else if (phase === 'target') {
                // Backed out of every target pick — return to picking sources.
                phase = 'source';
                targetSplat = null;
            } else if (srcPicks.length > 0) {
                srcPicks.pop();
            } else {
                return;
            }
            rebuildOverlay();
            fireChanged();
            status(guidance());
        };

        const undoAlign = () => {
            if (!previous) {
                status('Nothing to undo.');
                return;
            }
            previous.splat.move(previous.position, previous.rotation, previous.scale);
            scene.boundDirty = true;
            scene.forceRender = true;
            previous = null;
            status('Reverted the splat to its previous transform.');
            fireChanged();
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (!active) return;
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                events.fire('tool.deactivate');
            } else if (e.key === 'Backspace' || e.key === 'Delete') {
                // Capture phase on window so the global delete shortcut never
                // sees these while the tool owns them.
                e.stopImmediatePropagation();
                e.preventDefault();
                undoPick();
            } else if (e.key === 'Enter') {
                e.stopImmediatePropagation();
                e.preventDefault();
                // Enter moves the flow on: finish this side, or apply.
                if (phase === 'source') switchToTarget();
                else if (pairCount() >= MIN_PAIRS && srcPicks.length === dstPicks.length) apply();
            }
        };

        const onContextMenu = (e: MouseEvent) => { if (active) e.preventDefault(); };

        events.on('align.pick.undo', undoPick);
        events.on('align.reset', () => { resetPicks(); status(guidance()); });
        events.on('align.switchToTarget', switchToTarget);
        events.on('align.apply', apply);
        events.on('align.undo', undoAlign);

        this.activate = () => {
            const selected = events.invoke('selection') as Splat | null;
            if (!selected) {
                events.fire('align.panel.show');
                status('Select the splat you want to MOVE first (click it in the scene list), then start aligning.');
                setTimeout(() => events.fire('tool.deactivate'), 0);
                return;
            }
            active = true;
            sourceSplat = selected;
            resetPicks();
            buildOverlay();
            canvas.style.cursor = 'crosshair';
            canvas.addEventListener('pointerdown', onPointerDown, true);
            canvas.addEventListener('contextmenu', onContextMenu);
            window.addEventListener('keydown', onKeyDown, true);
            events.fire('align.panel.show');
            events.fire('align.tool.active', true);
            fireChanged();
            status(guidance());
        };

        this.deactivate = () => {
            active = false;
            canvas.style.cursor = '';
            canvas.removeEventListener('pointerdown', onPointerDown, true);
            canvas.removeEventListener('contextmenu', onContextMenu);
            window.removeEventListener('keydown', onKeyDown, true);
            resetPicks();
            if (entity) { entity.destroy(); entity = null; mesh = null; }
            events.fire('align.tool.active', false);
        };
    }
}

export { AlignTool };
